/**
 * MechaGuard - Projectile (投射物)
 *
 * 支持两种模式：
 *   - 'homing' : 追踪目标飞行（等离子炮），命中后可选范围溅射
 *   - 'linear' : 沿固定方向直飞（电磁轨道炮），可贯穿多个目标
 *
 * 伤害结算统一走 game.damageEnemy()，因此护甲、击杀奖励、
 * 特效生成都在一处处理，投射物本身不关心这些细节。
 */

export class Projectile {
  /**
   * @param {object} opts
   * @param {'homing'|'linear'} [opts.mode]
   * @param {number} opts.x
   * @param {number} opts.y
   * @param {import('./Enemy.js').Enemy} [opts.target] 追踪模式必需
   * @param {number} [opts.angle]  直线模式必需
   * @param {import('./Tower.js').Tower} [opts.source] 开火的塔（用于累计伤害统计）
   * @param {number} opts.speed
   * @param {number} opts.damage
   * @param {number} [opts.ignoreArmor=0]
   * @param {number} [opts.splashRadius=0]
   * @param {number} [opts.splashFactor=0]
   * @param {number} [opts.pierce=0]  可额外贯穿的目标数
   * @param {number} [opts.maxDistance=1200]
   * @param {string} [opts.color='#ffffff']
   * @param {number} [opts.size=4]
   * @param {boolean} [opts.trail=false]
   */
  constructor(opts) {
    this.mode = opts.mode === 'linear' ? 'linear' : 'homing';

    this.x = opts.x;
    this.y = opts.y;
    this.source = opts.source || null;

    this.speed = opts.speed;
    this.damage = opts.damage;
    this.ignoreArmor = opts.ignoreArmor || 0;

    this.splashRadius = opts.splashRadius || 0;
    this.splashFactor = opts.splashFactor || 0;
    this.pierce = opts.pierce || 0;
    this.maxDistance = opts.maxDistance || 1200;

    this.color = opts.color || '#ffffff';
    this.size = opts.size || 4;
    this.useTrail = !!opts.trail;
    /** 'missile' 时绘制为火箭造型（默认沿用原画法） */
    this.shape = opts.shape || null;

    /** 已命中过的目标（防止穿透弹重复伤害同一敌人） */
    this.hitSet = new Set();

    this.dead = false;
    this.age = 0;
    this.traveled = 0;
    this.maxAge = 5;

    if (this.mode === 'homing') {
      this.target = opts.target || null;
      this.angle = this.target
        ? Math.atan2(this.target.y - this.y, this.target.x - this.x)
        : 0;
    } else {
      this.target = null;
      this.angle = opts.angle || 0;
    }

    /** @type {{x:number,y:number}[]|null} */
    this.trailPoints = this.useTrail ? [{ x: this.x, y: this.y }] : null;
  }

  /* ===================================================
   * 更新
   * =================================================== */

  /**
   * @param {number} dt
   * @param {import('../core/Game.js').Game} game
   */
  update(dt, game) {
    // 已经阵亡的投射物不再推进。
    //
    // 正常流程里 Game._cleanup() 每帧都会把 dead 的投射物移出数组，
    // 所以这一行是冗余保险。但它把「dead 就是不再参与模拟」变成一条
    // 真实不变量 —— 否则单独使用这个类（例如单元测试）时，
    // 弹丸会在 dead 之后继续飞行、继续命中，甚至反复引爆。
    if (this.dead) return;

    this.age += dt;
    if (this.age > this.maxAge) {
      this.dead = true;
      return;
    }

    const step = this.speed * dt;
    if (step <= 0) return;

    if (this.mode === 'homing') {
      this._updateHoming(step, game);
    } else {
      this._updateLinear(step, game);
    }

    this._recordTrail();
  }

  _updateHoming(step, game) {
    const t = this.target;

    // 目标在弹丸抵达前消失（被其它塔击杀 / 已进入基地）
    if (!t || !t.alive || t.reachedBase) {
      // 带溅射的弹种就地炸开，避免这一发的范围伤害被凭空吞掉；
      // 纯单体弹种则直接湮灭。
      if (this.splashRadius > 0) this._splashAt(game, this.x, this.y, null);
      this.dead = true;
      return;
    }

    const dx = t.x - this.x;
    const dy = t.y - this.y;
    const dist = Math.hypot(dx, dy);

    this.angle = Math.atan2(dy, dx);

    if (dist <= step + t.radius) {
      this.x = t.x;
      this.y = t.y;
      this._detonate(game, t);
      this.dead = true;
      return;
    }

    this.x += (dx / dist) * step;
    this.y += (dy / dist) * step;
    this.traveled += step;
  }

  _updateLinear(step, game) {
    const prevX = this.x;
    const prevY = this.y;

    this.x += Math.cos(this.angle) * step;
    this.y += Math.sin(this.angle) * step;
    this.traveled += step;

    const enemies = game.enemies;

    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i];
      if (!e.alive || e.reachedBase) continue;
      if (this.hitSet.has(e)) continue;

      // 高速弹丸单帧位移可能超过敌人直径（1400px/s 在 50ms 帧里走 70px），
      // 点检测会直接穿模，必须用「线段 vs 圆」的扫掠检测。
      const rr = e.radius + this.size;
      if (!segmentCircleHit(prevX, prevY, this.x, this.y, e.x, e.y, rr)) continue;

      this.hitSet.add(e);

      const dealt = game.damageEnemy(e, this.damage, { ignoreArmor: this.ignoreArmor });
      if (this.source) this.source.totalDamage += dealt;

      game.particles.spawnHit(e.x, e.y, this.color, 4);

      // pierce 表示可额外贯穿的目标数 -> 总共可命中 pierce + 1 个
      if (this.hitSet.size > this.pierce) {
        this.dead = true;
        return;
      }
    }

    if (this.traveled >= this.maxDistance) this.dead = true;
  }

  /** 追踪弹命中：主目标满伤 + 可选范围溅射 */
  _detonate(game, primary) {
    const dealt = game.damageEnemy(primary, this.damage, { ignoreArmor: this.ignoreArmor });
    if (this.source) this.source.totalDamage += dealt;

    if (this.splashRadius <= 0) {
      game.particles.spawnHit(this.x, this.y, this.color, 6);
      return;
    }

    this._splashAt(game, this.x, this.y, primary);
  }

  /**
   * 在指定位置结算范围溅射
   * @param {import('../core/Game.js').Game} game
   * @param {number} x
   * @param {number} y
   * @param {import('./Enemy.js').Enemy|null} exclude 已单独结算过满伤的目标
   */
  _splashAt(game, x, y, exclude) {
    const r2 = this.splashRadius * this.splashRadius;
    const splashDamage = this.damage * this.splashFactor;
    const enemies = game.enemies;

    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i];
      if (e === exclude || !e.alive || e.reachedBase) continue;
      if (e.distanceSqTo(x, y) > r2) continue;

      const d = game.damageEnemy(e, splashDamage, { ignoreArmor: this.ignoreArmor });
      if (this.source) this.source.totalDamage += d;
    }

    game.effects.addExplosion(x, y, this.splashRadius, this.color, 0.32);
    game.particles.spawnBurst(x, y, {
      count: 14,
      color: this.color,
      speed: 160,
      life: 0.45,
      size: 3,
    });
    game.sfx('explosion');
  }

  _recordTrail() {
    if (!this.trailPoints) return;

    const last = this.trailPoints[this.trailPoints.length - 1];
    if (Math.hypot(this.x - last.x, this.y - last.y) >= 6) {
      this.trailPoints.push({ x: this.x, y: this.y });
      if (this.trailPoints.length > 9) this.trailPoints.shift();
    }
  }

  /* ===================================================
   * 绘制
   * =================================================== */

  draw(ctx) {
    // 拖尾
    if (this.trailPoints && this.trailPoints.length > 1) {
      ctx.save();
      ctx.globalAlpha = 0.4;
      ctx.strokeStyle = this.color;
      ctx.lineWidth = this.size * 0.9;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(this.trailPoints[0].x, this.trailPoints[0].y);
      for (let i = 1; i < this.trailPoints.length; i++) {
        ctx.lineTo(this.trailPoints[i].x, this.trailPoints[i].y);
      }
      ctx.stroke();
      ctx.restore();
    }

    ctx.save();
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 12;

    if (this.shape === 'missile') {
      // 导弹：白色弹体 + 塔色鼻锥/尾翼/尾焰，沿飞行方向
      ctx.translate(this.x, this.y);
      ctx.rotate(this.angle);
      const L = this.size * 2.2;   // 半长
      const W = this.size * 0.85;  // 半宽

      // 尾焰
      ctx.fillStyle = this.color;
      ctx.beginPath();
      ctx.moveTo(-L, -W * 0.6);
      ctx.lineTo(-L - this.size * 1.8, 0);
      ctx.lineTo(-L, W * 0.6);
      ctx.closePath();
      ctx.fill();

      // 弹体（白色为主）
      const grad = ctx.createLinearGradient(0, -W, 0, W);
      grad.addColorStop(0, '#ffffff');
      grad.addColorStop(0.5, '#eef2f7');
      grad.addColorStop(1, '#c9d3df');
      ctx.fillStyle = grad;
      ctx.strokeStyle = '#9fb0c3';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(-L, -W);
      ctx.lineTo(L * 0.6, -W);
      ctx.quadraticCurveTo(L, -W, L, 0);          // 鼻锥
      ctx.quadraticCurveTo(L, W, L * 0.6, W);
      ctx.lineTo(-L, W);
      ctx.quadraticCurveTo(-L - 3, 0, -L, -W);    // 尾部收口
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // 鼻锥尖端（塔色）
      ctx.fillStyle = this.color;
      ctx.beginPath();
      ctx.arc(L, 0, this.size * 0.45, 0, Math.PI * 2);
      ctx.fill();

      // 尾翼（塔色）
      for (const s of [-1, 1]) {
        ctx.beginPath();
        ctx.moveTo(-L, s * W);
        ctx.lineTo(-L + this.size * 0.9, s * W);
        ctx.lineTo(-L, s * W * 1.9);
        ctx.closePath();
        ctx.fill();
      }
    } else if (this.mode === 'linear') {
      // 拉长的穿甲弹
      ctx.translate(this.x, this.y);
      ctx.rotate(this.angle);
      ctx.fillStyle = this.color;
      ctx.fillRect(-this.size * 3, -this.size * 0.5, this.size * 6, this.size);
    } else {
      ctx.fillStyle = this.color;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}

/**
 * 线段 (x0,y0)-(x1,y1) 与圆 (cx,cy,r) 是否相交。
 * 用于高速弹丸的扫掠碰撞检测，避免单帧位移过大导致穿模。
 * @returns {boolean}
 */
function segmentCircleHit(x0, y0, x1, y1, cx, cy, r) {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const lenSq = dx * dx + dy * dy;
  const rSq = r * r;

  // 位移为 0：退化为点检测
  if (lenSq <= 1e-9) {
    const px = cx - x0;
    const py = cy - y0;
    return px * px + py * py <= rSq;
  }

  // 把圆心投影到线段上，并夹取到 [0,1] 区间
  let t = ((cx - x0) * dx + (cy - y0) * dy) / lenSq;
  t = t < 0 ? 0 : (t > 1 ? 1 : t);

  const nx = x0 + dx * t;
  const ny = y0 + dy * t;
  const ddx = cx - nx;
  const ddy = cy - ny;

  return ddx * ddx + ddy * ddy <= rSq;
}
